// store/modules/importaciones.js
import apiClient from "../libs/axios";
import { apiUrl } from "../constans";

const importaciones = {
    namespaced: true,

    state: {
        // Logs de importaciones
        importaciones: [],
        importacionActual: null,

        // Resumen y estadísticas
        resumen: {
            total_importaciones: 0,
            total_contactos_procesados: 0,
            total_contactos_exitosos: 0,
            total_contactos_fallidos: 0,
            tasa_exito_promedio: 0
        },

        estadisticasGenerales: {
            total_importaciones: 0,
            total_contactos_procesados: 0,
            total_contactos_exitosos: 0,
            total_contactos_fallidos: 0,
            tasa_exito_global: 0,
            por_tipo: {},
            top_usuarios: []
        },

        // Paginación
        pagination: {
            current_page: 1,
            last_page: 1,
            per_page: 20,
            total: 0
        },

        // Estado de carga
        loading: false,
        uploadProgress: 0,

        // Filtros
        filters: {
            tipo: '',
            periodo: 'hoy',
            desde: '',
            hasta: ''
        }
    },

    mutations: {
        // Setters de datos
        setImportaciones(state, importaciones) {
            state.importaciones = importaciones;
        },

        setImportacionActual(state, importacion) {
            state.importacionActual = importacion;
        },

        agregarImportacion(state, importacion) {
            state.importaciones.unshift(importacion);
        },

        actualizarImportacion(state, importacion) {
            const index = state.importaciones.findIndex(i => i.id === importacion.id);
            if (index !== -1) {
                state.importaciones.splice(index, 1, importacion);
            }
        },

        eliminarImportacion(state, logId) {
            state.importaciones = state.importaciones.filter(i => i.id !== logId);
        },

        setResumen(state, resumen) {
            state.resumen = resumen;
        },

        setEstadisticasGenerales(state, estadisticas) {
            state.estadisticasGenerales = estadisticas;
        },

        setPagination(state, pagination) {
            state.pagination = pagination;
        },

        setLoading(state, loading) {
            state.loading = loading;
        },

        setUploadProgress(state, progress) {
            state.uploadProgress = progress;
        },

        setFilters(state, filters) {
            state.filters = { ...state.filters, ...filters };
        },

        resetFilters(state) {
            state.filters = {
                tipo: '',
                periodo: 'hoy',
                desde: '',
                hasta: ''
            };
        }
    },

    getters: {
        // Getters básicos
        getImportaciones: (state) => state.importaciones,
        getImportacionActual: (state) => state.importacionActual,
        getResumen: (state) => state.resumen,
        getEstadisticasGenerales: (state) => state.estadisticasGenerales,
        getPagination: (state) => state.pagination,
        getLoading: (state) => state.loading,
        getUploadProgress: (state) => state.uploadProgress,
        getFilters: (state) => state.filters,

        // Getters computados
        getImportacionesPorTipo: (state) => (tipo) => {
            return state.importaciones.filter(i => i.tipo_importacion === tipo);
        },

        getImportacionesExitosas: (state) => {
            return state.importaciones.filter(i => i.procesadas_fallidas === 0);
        },

        getImportacionesConErrores: (state) => {
            return state.importaciones.filter(i => i.procesadas_fallidas > 0);
        },

        getTotalContactosProcesados: (state) => {
            return state.importaciones.reduce((sum, i) => sum + (i.procesadas_exitosas || 0), 0);
        },

        getTotalContactosFallidos: (state) => {
            return state.importaciones.reduce((sum, i) => sum + (i.procesadas_fallidas || 0), 0);
        }
    },

    actions: {
        /**
         * Importar archivo según el tipo
         */
        importarArchivo({ commit }, { tipo, file, onProgress }) {
            return new Promise((resolve, reject) => {
                commit('setLoading', true);
                commit('setUploadProgress', 0);

                const formData = new FormData();
                formData.append('file', file);

                // Mapeo de tipos a endpoints
                const endpoints = {
                    'inmuebles-links-vistas': 'subportales/import-contact-view',
                    'contactos-proppit': 'subportales/import-proppit',
                    'contactos-ciencuadra': 'clientes-import-ciencuadra',
                    'contactos-metrocuadrado': 'clientes-import-metrocuadrado',
                    'contactos-fincaraiz': 'clientes-import-fincaraiz'
                };

                const endpoint = endpoints[tipo];

                if (!endpoint) {
                    commit('setLoading', false);
                    reject(new Error('Tipo de importación no válido'));
                    return;
                }

                apiClient.post(`${apiUrl}/v2/import/${endpoint}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        commit('setUploadProgress', percentCompleted);
                        if (onProgress) onProgress(percentCompleted);
                    }
                })
                .then(response => {
                    commit('setLoading', false);
                    commit('setUploadProgress', 100);

                    if (response.data.success) {
                        // Agregar la nueva importación al estado
                        if (response.data.data) {
                            commit('agregarImportacion', {
                                id: response.data.data.log_id,
                                ...response.data.data
                            });
                        }
                    }

                    resolve(response.data);
                })
                .catch(error => {
                    commit('setLoading', false);
                    commit('setUploadProgress', 0);
                    reject(error);
                });
            });
        },

        /**
         * Obtener estado de una importación específica
         */
        obtenerEstadoImportacion({ commit }, logId) {
            return new Promise((resolve, reject) => {
                apiClient.get(`${apiUrl}/v2/import/importaciones/${logId}/estado`)
                    .then(response => {
                        if (response.data.success) {
                            commit('setImportacionActual', response.data.data);
                            commit('actualizarImportacion', response.data.data);
                        }
                        resolve(response.data);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },

        /**
         * Obtener reporte diario
         */
        obtenerReporteDiario({ commit, state }) {
            return new Promise((resolve, reject) => {
                commit('setLoading', true);

                const params = {};
                if (state.filters.tipo) {
                    params.tipo = state.filters.tipo;
                }

                apiClient.get(`${apiUrl}/v2/import/reportes/importaciones/diario`, { params })
                    .then(response => {
                        commit('setLoading', false);

                        if (response.data.success) {
                            commit('setImportaciones', response.data.importaciones || []);
                            commit('setResumen', response.data.resumen || {});
                        }

                        resolve(response.data);
                    })
                    .catch(error => {
                        commit('setLoading', false);
                        reject(error);
                    });
            });
        },

        /**
         * Obtener reporte mensual
         */
        obtenerReporteMensual({ commit, state }) {
            return new Promise((resolve, reject) => {
                commit('setLoading', true);

                const params = {};
                if (state.filters.tipo) {
                    params.tipo = state.filters.tipo;
                }

                apiClient.get(`${apiUrl}/v2/import/reportes/importaciones/mensual`, { params })
                    .then(response => {
                        commit('setLoading', false);

                        if (response.data.success) {
                            commit('setImportaciones', response.data.importaciones || []);
                            commit('setResumen', response.data.resumen_general || {});
                        }

                        resolve(response.data);
                    })
                    .catch(error => {
                        commit('setLoading', false);
                        reject(error);
                    });
            });
        },

        /**
         * Obtener historial con filtros y paginación
         */
        obtenerHistorial({ commit, state }, page = 1) {
            return new Promise((resolve, reject) => {
                commit('setLoading', true);

                const params = {
                    page,
                    per_page: state.pagination.per_page
                };

                if (state.filters.tipo) {
                    params.tipo = state.filters.tipo;
                }

                if (state.filters.desde) {
                    params.desde = state.filters.desde;
                }

                if (state.filters.hasta) {
                    params.hasta = state.filters.hasta;
                }

                apiClient.get(`${apiUrl}/v2/import/reportes/importaciones/historial`, { params })
                    .then(response => {
                        commit('setLoading', false);

                        if (response.data.success) {
                            commit('setImportaciones', response.data.data || []);

                            if (response.data.pagination) {
                                commit('setPagination', response.data.pagination);
                            }
                        }

                        resolve(response.data);
                    })
                    .catch(error => {
                        commit('setLoading', false);
                        reject(error);
                    });
            });
        },

        /**
         * Obtener estadísticas generales
         */
        obtenerEstadisticasGenerales({ commit }, { desde = null, hasta = null } = {}) {
            return new Promise((resolve, reject) => {
                const params = {};
                if (desde) params.desde = desde;
                if (hasta) params.hasta = hasta;

                apiClient.get(`${apiUrl}/v2/import/reportes/importaciones/estadisticas`, { params })
                    .then(response => {
                        if (response.data.success) {
                            commit('setEstadisticasGenerales', response.data.estadisticas || {});
                        }
                        resolve(response.data);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },

        /**
         * Eliminar un log de importación
         */
        eliminarLog({ commit }, logId) {
            return new Promise((resolve, reject) => {
                apiClient.delete(`${apiUrl}/v2/import/importaciones/${logId}`)
                    .then(response => {
                        if (response.data.success) {
                            commit('eliminarImportacion', logId);
                        }
                        resolve(response.data);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },

        /**
         * Aplicar filtros y recargar datos
         */
        aplicarFiltros({ commit, dispatch }, filtros) {
            commit('setFilters', filtros);

            // Determinar qué acción ejecutar según el período
            if (filtros.periodo === 'hoy') {
                return dispatch('obtenerReporteDiario');
            } else if (filtros.periodo === 'mes') {
                return dispatch('obtenerReporteMensual');
            } else {
                return dispatch('obtenerHistorial');
            }
        },

        /**
         * Limpiar filtros
         */
        limpiarFiltros({ commit, dispatch }) {
            commit('resetFilters');
            return dispatch('obtenerReporteDiario');
        },

        /**
         * Polling para verificar estado de importación en proceso
         */
        iniciarPollingImportacion({ dispatch }, { logId, intervalo = 3000, maxIntentos = 20 }) {
            let intentos = 0;

            return new Promise((resolve, reject) => {
                const interval = setInterval(async () => {
                    try {
                        intentos++;
                        const response = await dispatch('obtenerEstadoImportacion', logId);

                        if (response.data.estado === 'completado' || response.data.estado === 'error') {
                            clearInterval(interval);
                            resolve(response.data);
                        }

                        if (intentos >= maxIntentos) {
                            clearInterval(interval);
                            reject(new Error('Tiempo máximo de espera excedido'));
                        }
                    } catch (error) {
                        clearInterval(interval);
                        reject(error);
                    }
                }, intervalo);
            });
        }
    }
};

export default importaciones;
